import React, { useCallback, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Clock3,
  Download,
  MessageSquare,
} from 'lucide-react';
import useStoreReviews, { useReplyToReview } from '../hooks/useStoreReviews';
import useServiceCatalog from '../../offers/hooks/useServiceCatalog';
import ReviewFilters from '../components/ReviewFilters';
import ReviewList from '../components/ReviewList';
import StarRating from '../components/StarRating';
import { downloadCsv } from '../../analytics/utils/csvExport';
import './CustomerReviews.css';

const PAGE_SIZE = 20;
const RATING_KEYS = [5, 4, 3, 2, 1];

const normalizeServiceOptions = (services = []) => (
  services
    .map((service) => {
      const value = service?.serviceUuid || service?.uuid || service?.value;
      const label = service?.serviceName || service?.name || service?.label;
      if (!value || !label) return null;
      return { value: String(value), label: String(label) };
    })
    .filter(Boolean)
);

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeDistribution = (distribution = {}) => (
  RATING_KEYS.reduce((accumulator, rating) => {
    accumulator[rating] = toNumber(distribution?.[rating] ?? distribution?.[String(rating)], 0);
    return accumulator;
  }, {})
);

const getReviewTime = (review) => {
  const parsed = new Date(review?.createdAt);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const formatDate = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDuration = (milliseconds) => {
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) {
    return '—';
  }
  const minutes = Math.round(milliseconds / 60000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
};

const matchesSearch = (review, query) => {
  const normalizedQuery = String(query || '').trim().toLowerCase();
  if (!normalizedQuery) return true;
  const fields = [
    review.serviceName,
    review.serviceUuid,
    review.comment,
    review.customerPhoneMasked,
    review.appointmentUuid,
    review.merchantReply,
    ...(Array.isArray(review.tags) ? review.tags : []),
  ];
  return fields.some((field) => String(field || '').toLowerCase().includes(normalizedQuery));
};

const buildVisibleInsights = (reviews = []) => {
  const total = reviews.length;
  const repliedReviews = reviews.filter((review) => Boolean(review.merchantReply));
  const responseRate = total > 0 ? Math.round((repliedReviews.length / total) * 100) : 0;
  const replyDurations = repliedReviews
    .map((review) => {
      const createdAt = new Date(review.createdAt).getTime();
      const repliedAt = new Date(review.repliedAt).getTime();
      return Number.isFinite(createdAt) && Number.isFinite(repliedAt) ? repliedAt - createdAt : null;
    })
    .filter((value) => Number.isFinite(value) && value > 0);
  const avgFirstResponseMs = replyDurations.length > 0
    ? replyDurations.reduce((sum, value) => sum + value, 0) / replyDurations.length
    : null;
  const awaitingReviews = reviews
    .filter((review) => !review.merchantReply)
    .sort((a, b) => getReviewTime(b) - getReviewTime(a));

  const tagCounts = new Map();
  reviews.forEach((review) => {
    (Array.isArray(review.tags) ? review.tags : []).forEach((tag) => {
      const label = String(tag || '').trim();
      if (!label) return;
      tagCounts.set(label, (tagCounts.get(label) || 0) + 1);
    });
  });

  const topMentions = Array.from(tagCounts.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .slice(0, 5);

  return {
    total,
    responseRate,
    avgFirstResponseLabel: formatDuration(avgFirstResponseMs),
    awaitingReviews,
    awaitingCount: awaitingReviews.length,
    topMentions,
  };
};

const exportReviewsCsv = (reviews = []) => {
  const rows = reviews.map((review) => ({
    'Review UUID': review.reviewUuid,
    Service: review.serviceName || review.serviceUuid || '',
    Rating: review.rating,
    Comment: review.comment,
    Tags: Array.isArray(review.tags) ? review.tags.join(', ') : '',
    Customer: review.customerPhoneMasked,
    'Created at': review.createdAt,
    'Merchant reply': review.merchantReply,
    'Replied at': review.repliedAt,
  }));
  downloadCsv(
    `customer-reviews-${new Date().toISOString().slice(0, 10)}.csv`,
    rows,
    { title: 'Customer reviews' }
  );
};

const CustomerReviews = ({ request, storeToken, onToast, storeProfile }) => {
  const [filters, setFilters] = useState({
    search: '',
    serviceUuid: '',
    rating: '',
    sort: 'newest',
    page: 0,
  });

  const { services, isLoading: isLoadingServices } = useServiceCatalog({
    request,
    storeToken,
    onToast,
  });

  const serviceOptions = useMemo(() => normalizeServiceOptions(services), [services]);
  const serviceLabelByUuid = useMemo(() => (
    serviceOptions.reduce((accumulator, option) => {
      accumulator.set(option.value, option.label);
      return accumulator;
    }, new Map())
  ), [serviceOptions]);

  const { summary, reviews, pagination, isLoading, error, reload, replaceReview } = useStoreReviews({
    request,
    filters: {
      page: filters.page,
      size: PAGE_SIZE,
      serviceUuid: filters.serviceUuid || null,
      rating: filters.rating || null,
      sort: filters.sort || 'newest',
    },
  });
  const reviewsWithServiceLabels = useMemo(() => (
    reviews.map((review) => {
      if (review?.serviceName || !review?.serviceUuid) {
        return review;
      }
      const serviceName = serviceLabelByUuid.get(String(review.serviceUuid));
      return serviceName ? { ...review, serviceName } : review;
    })
  ), [reviews, serviceLabelByUuid]);
  const visibleReviews = useMemo(() => (
    reviewsWithServiceLabels.filter((review) => matchesSearch(review, filters.search))
  ), [reviewsWithServiceLabels, filters.search]);
  const visibleInsights = useMemo(() => buildVisibleInsights(visibleReviews), [visibleReviews]);
  const distribution = useMemo(
    () => normalizeDistribution(summary?.ratingDistribution),
    [summary?.ratingDistribution]
  );
  const totalReviewCount = toNumber(summary?.totalReviewCount, 0);
  const averageRating = toNumber(summary?.averageRating, 0);
  const serviceCount = serviceOptions.length;
  const maxDistribution = Math.max(1, ...Object.values(distribution));
  const sentimentCounts = useMemo(() => {
    const positive = distribution[5] + distribution[4];
    const neutral = distribution[3];
    const critical = distribution[2] + distribution[1];
    const total = Math.max(1, positive + neutral + critical);
    return {
      positive,
      neutral,
      critical,
      positivePct: Math.round((positive / total) * 100),
      neutralPct: Math.round((neutral / total) * 100),
      criticalPct: Math.round((critical / total) * 100),
    };
  }, [distribution]);
  const storeName = storeProfile?.storeName;

  const handleReplySuccess = useCallback((updatedReview) => {
    if (updatedReview?.reviewUuid) {
      replaceReview(updatedReview);
    }
    reload();
  }, [reload, replaceReview]);

  const { replyToReview, isSavingReview } = useReplyToReview({
    request,
    onToast,
    onSuccess: handleReplySuccess,
  });

  const updateFilter = (key, value) => {
    setFilters((previous) => ({
      ...previous,
      [key]: value,
      page: 0,
    }));
  };

  const totalPages = Math.max(1, Number(pagination?.totalPages || 1));
  const currentPage = Number(pagination?.page ?? filters.page ?? 0);
  const canGoBack = currentPage > 0;
  const canGoForward = currentPage + 1 < totalPages;

  return (
    <div className="customer-reviews">
      <header className="customer-reviews__header">
        <div>
          <h2 className="customer-reviews__title">Customer Reviews</h2>
          <p className="customer-reviews__subtitle">
            {totalReviewCount} total review{totalReviewCount === 1 ? '' : 's'} across {serviceCount} service{serviceCount === 1 ? '' : 's'} · {visibleReviews.length} visible
          </p>
        </div>
        <button
          type="button"
          className="customer-reviews__export"
          onClick={() => exportReviewsCsv(visibleReviews)}
          disabled={visibleReviews.length === 0}
        >
          <Download size={15} />
          Export CSV
        </button>
      </header>

      <section className="customer-reviews__overview" aria-label="Review performance summary">
        <div className="customer-reviews__rating-panel">
          <span className="customer-reviews__eyebrow">Overall Rating</span>
          <div className="customer-reviews__score-line">
            <span className="customer-reviews__score">{averageRating.toFixed(1)}</span>
            <span className="customer-reviews__score-max">/ 5</span>
          </div>
          <StarRating value={averageRating} size={16} />
          <span className="customer-reviews__based-on">
            Based on {totalReviewCount} review{totalReviewCount === 1 ? '' : 's'}
          </span>
          {summary?.lastReviewDate && (
            <span className="customer-reviews__last-review">
              Latest review {formatDate(summary.lastReviewDate)}
            </span>
          )}
        </div>

        <div className="customer-reviews__distribution-panel">
          <span className="customer-reviews__eyebrow">Rating Distribution</span>
          <div className="customer-reviews__distribution">
            {RATING_KEYS.map((rating) => {
              const count = distribution[rating];
              return (
                <div key={rating} className="customer-reviews__distribution-row">
                  <span className="customer-reviews__distribution-label">{rating} ★</span>
                  <span className="customer-reviews__distribution-track">
                    <span
                      className={rating <= 2 ? 'customer-reviews__distribution-fill customer-reviews__distribution-fill--critical' : 'customer-reviews__distribution-fill'}
                      style={{ width: `${Math.round((count / maxDistribution) * 100)}%` }}
                    />
                  </span>
                  <span className="customer-reviews__distribution-count">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="customer-reviews__metric-panel">
          <div className="customer-reviews__metric">
            <div>
              <span className="customer-reviews__metric-label">Response rate</span>
              <strong>{visibleInsights.responseRate}%</strong>
            </div>
            <MessageSquare size={18} />
          </div>
          <div className="customer-reviews__metric">
            <div>
              <span className="customer-reviews__metric-label">Avg. first response</span>
              <strong>{visibleInsights.avgFirstResponseLabel}</strong>
            </div>
            <Clock3 size={18} />
          </div>
          <div className="customer-reviews__metric">
            <div>
              <span className="customer-reviews__metric-label">Awaiting reply</span>
              <strong>{visibleInsights.awaitingCount}</strong>
            </div>
            <AlertTriangle size={18} />
          </div>
        </div>
      </section>

      <ReviewFilters
        services={serviceOptions}
        searchQuery={filters.search}
        serviceUuid={filters.serviceUuid}
        rating={filters.rating}
        sort={filters.sort}
        disabled={isLoading || isLoadingServices}
        onSearchChange={(value) => updateFilter('search', value)}
        onServiceChange={(value) => updateFilter('serviceUuid', value)}
        onRatingChange={(value) => updateFilter('rating', value)}
        onSortChange={(value) => updateFilter('sort', value)}
      />

      {isLoading && <p className="customer-reviews__state">Loading reviews…</p>}
      {!isLoading && error && (
        <p className="customer-reviews__state customer-reviews__state--error">{error}</p>
      )}
      {!isLoading && !error && (
        <div className="customer-reviews__content-grid">
          <ReviewList
            reviews={visibleReviews}
            storeName={storeName}
            onReplySave={replyToReview}
            isReplySaving={isSavingReview}
          />

          <aside className="customer-reviews__side-rail" aria-label="Review insights">
            <section className="customer-reviews__side-card">
              <div className="customer-reviews__side-card-head">
                <h3>Awaiting response</h3>
                <span>{visibleInsights.awaitingCount}</span>
              </div>
              {visibleInsights.awaitingReviews.length > 0 ? (
                <div className="customer-reviews__awaiting-list">
                  {visibleInsights.awaitingReviews.slice(0, 4).map((review) => (
                    <div key={review.reviewUuid} className="customer-reviews__awaiting-item">
                      <span>{review.rating}</span>
                      <div>
                        <strong>{review.serviceName || review.serviceUuid}</strong>
                        <small>{review.customerPhoneMasked || review.appointmentUuid} · {formatDate(review.createdAt)}</small>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="customer-reviews__side-empty">All visible reviews have a response.</p>
              )}
            </section>

            <section className="customer-reviews__side-card">
              <h3>Top mentions</h3>
              {visibleInsights.topMentions.length > 0 ? (
                <div className="customer-reviews__mention-list">
                  {visibleInsights.topMentions.map((mention) => (
                    <div key={mention.label} className="customer-reviews__mention-row">
                      <div>
                        <span>{mention.label}</span>
                        <small>{mention.count}</small>
                      </div>
                      <span className="customer-reviews__mention-track">
                        <span style={{ width: `${Math.round((mention.count / Math.max(1, visibleInsights.topMentions[0].count)) * 100)}%` }} />
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="customer-reviews__side-empty">Mentions will appear when reviews include tags.</p>
              )}
            </section>

            <section className="customer-reviews__side-card">
              <h3>Sentiment split</h3>
              <div className="customer-reviews__sentiment-bar" aria-hidden="true">
                <span style={{ width: `${sentimentCounts.positivePct}%` }} />
                <span style={{ width: `${sentimentCounts.neutralPct}%` }} />
                <span style={{ width: `${sentimentCounts.criticalPct}%` }} />
              </div>
              <div className="customer-reviews__sentiment-list">
                <span><i className="is-positive" /> Positive <strong>{sentimentCounts.positivePct}%</strong></span>
                <span><i className="is-neutral" /> Neutral <strong>{sentimentCounts.neutralPct}%</strong></span>
                <span><i className="is-critical" /> Critical <strong>{sentimentCounts.criticalPct}%</strong></span>
              </div>
            </section>
          </aside>
        </div>
      )}
      {!isLoading && !error && visibleReviews.length > 0 && (
        <div className="customer-reviews__pagination" aria-label="Reviews pagination">
          <button
            type="button"
            className="customer-reviews__page-button"
            disabled={!canGoBack}
            onClick={() => setFilters((previous) => ({ ...previous, page: Math.max(0, previous.page - 1) }))}
          >
            Prev
          </button>
          <span className="customer-reviews__page-count">
            Page {currentPage + 1} of {totalPages}
          </span>
          <button
            type="button"
            className="customer-reviews__page-button"
            disabled={!canGoForward}
            onClick={() => setFilters((previous) => ({ ...previous, page: previous.page + 1 }))}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomerReviews;
